/**
 * **옛 서비스 주소 → 새 주소 표** — 정본은 `legacy-redirects.json` 한 벌이고 `next.config.mjs` 의
 * `redirects()` 가 그것을 그대로 308(영구)로 건다. 이 파일은 같은 표를 코드·테스트가 읽는 창구다.
 *
 * 🚨 **밖에서 들어오는 옛 링크가 이 표로 산다** — 백엔드 알림(`workspaceAlerts` 의 `cta.href`) ·
 *   홈 문서(`findHome` 의 `href`) · MCP 가 주는 앱 링크 · 발송한 메일 · 북마크. 줄을 지우면 404 다.
 * ⚠️ 질의 문자열은 Next 가 그대로 싣는다(`?q=` · `brand` · `tab` · `days` …) — 표의 목적지에는 질의를 적지 않는다.
 * ⚠️ 원천 주소는 **경로 전체가 맞아야** 걸린다 — `/service/search` 는 `/service/search-trend` 를 안 덮는다.
 */

import LEGACY_REDIRECTS_JSON from './legacy-redirects.json';

/** 표 한 줄 — Next `redirects()` 의 `source`/`destination` 꼴(`:id` 한 마디 · `:path+` 끝의 여러 마디) */
export type LegacyRedirect = { source: string; destination: string };

export const LEGACY_REDIRECTS: readonly LegacyRedirect[] = LEGACY_REDIRECTS_JSON;

/**
 * 주소 하나가 표의 어느 줄에 걸리는지 보고 새 주소를 짓는다 — Next 가 서버에서 하는 일과 같은 규칙.
 * @param url 경로 + 질의(`/service/brands?brand=메디큐브`)
 * @returns 새 주소(질의 그대로) · 표에 없으면 `null`
 */
export function resolveLegacyRedirect(url: string): string | null {
  const [pathname, ...rest] = String(url ?? '').split('?');
  const search = rest.length ? `?${rest.join('?')}` : '';
  const actual = pathname.replace(/\/+$/, '').split('/').filter(Boolean);

  for (const row of LEGACY_REDIRECTS) {
    const params = matchSource(row.source, actual);
    if (params) return `${fillDestination(row.destination, params)}${search}`;
  }
  return null;
}

function matchSource(source: string, actual: string[]): Record<string, string> | null {
  const pattern = source.split('/').filter(Boolean);
  const params: Record<string, string> = {};
  for (let i = 0; i < pattern.length; i += 1) {
    const seg = pattern[i];
    if (seg.startsWith(':') && seg.endsWith('+')) {
      if (i !== pattern.length - 1 || actual.length <= i) return null;
      params[seg.slice(1, -1)] = actual.slice(i).join('/');
      return params;
    }
    if (actual[i] === undefined) return null;
    if (seg.startsWith(':')) params[seg.slice(1)] = actual[i];
    else if (seg !== actual[i]) return null;
  }
  return pattern.length === actual.length ? params : null;
}

function fillDestination(destination: string, params: Record<string, string>): string {
  return destination.replace(/:([A-Za-z]+)\+?/g, (_, name: string) => params[name] ?? '');
}
